import {
  isFixedWebsiteCtaDestination,
  WEBSITE_CTA_DESTINATION,
  type WebsiteCtaDestination,
} from "@shared/websiteCtaDestinations";
import { toPublicTabKey } from "@shared/publicTabKey";
import { LEGAL_CONTENT_ANCHOR } from "@shared/publicLegalDocument";

// Internal-link support for CTA buttons across the public vault page.
//
// A CTA button's stored URL (business_slides.ctaUrl / campaign — reused as-is)
// is EITHER a plain external URL (https://…, mailto:, tel:) OR an internal
// token that points at another slide/offer on the same page:
//   "slide:<id>"      → a business_slide (a user-tab slide, an offer slide, or
//                        a client-feedback slide)
//   "campaign:<id>"   → an active offer campaign shown in Current Offers
//   "tab:<navKey>"     → a live middle-section tab, independent of its title
//   "receptionist"    → opens the Digital Receptionist chat panel
//   "receptionist:pricing" → opens the chat in the guided pricing conversation
//   "session:consultation" → opens the Consultation session card
//                        (name → contact → grounded pricing answer)
//   "booking"         → opens the owner's online booking link (Settings →
//                        Online Booking) in a new tab; renders nothing when
//                        the owner hasn't set a booking link
//
// Clicking an internal CTA smooth-scrolls the visitor to that specific slide:
// the Services band switches to the tab that owns it, advances that tab's
// carousel to the exact slide, and scrolls the section into view. That is driven
// by a window CustomEvent so any CTA (hero or in-card) can request it without
// prop-drilling through the tab tree.

export type VaultNavTarget =
  | { kind: "slide" | "campaign"; id: string }
  | { kind: "tab"; key: string }
  | { kind: "session"; purpose: "consultation" };

export type ParsedCta =
  | { type: "external"; url: string }
  | { type: "internal"; target: VaultNavTarget }
  | { type: "receptionist"; flow?: "pricing" }
  | { type: "session"; purpose: "consultation" }
  | { type: "booking" };

// The token the editors' Button Link pickers store when the owner points a
// button at their online booking link (business_profile.bookingUrl).
export const BOOKING_CTA = WEBSITE_CTA_DESTINATION.BOOKING satisfies WebsiteCtaDestination;

// The token seeded onto new accounts' hero CTA ("Get Our Pricing") and offered
// in the editor's Button Link picker: opens the Digital Receptionist in the
// guided pricing conversation.
export const RECEPTIONIST_PRICING_CTA =
  WEBSITE_CTA_DESTINATION.RECEPTIONIST_PRICING satisfies WebsiteCtaDestination;
export const CONSULTATION_SESSION_CTA =
  WEBSITE_CTA_DESTINATION.CONSULTATION_SESSION satisfies WebsiteCtaDestination;

// Parse a stored CTA url. Returns null for blank. Internal tokens keep the id as
// a string so matching is uniform (public content ids may be number or string).
export function parseCtaUrl(url: string | null | undefined, publicHomeHref = "/"): ParsedCta | null {
  const raw = (url ?? "").trim();
  if (!raw) return null;
  if (isFixedWebsiteCtaDestination(raw)) {
    switch (raw) {
      case WEBSITE_CTA_DESTINATION.RECEPTIONIST: return { type: "receptionist" };
      case RECEPTIONIST_PRICING_CTA: return { type: "receptionist", flow: "pricing" };
      case CONSULTATION_SESSION_CTA: return { type: "session", purpose: "consultation" };
      case BOOKING_CTA: return { type: "booking" };
      case WEBSITE_CTA_DESTINATION.HOME:
      case WEBSITE_CTA_DESTINATION.BLOG: return { type: "external", url: websiteCtaDestinationHref(raw, publicHomeHref)! };
    }
  }
  const tab = /^tab:(tab-\d+|current-offers|client-feedback|sessions)$/.exec(raw);
  if (/^page:[a-z0-9]+(?:-[a-z0-9]+)*$/.test(raw)) {
    return { type: "external", url: websiteCtaDestinationHref(raw, publicHomeHref)! };
  }
  if (tab) return { type: "internal", target: { kind: "tab", key: tab[1] } };
  const m = /^(slide|campaign):(.+)$/.exec(raw);
  if (m) {
    return { type: "internal", target: { kind: m[1] as "slide" | "campaign", id: m[2].trim() } };
  }
  return { type: "external", url: raw };
}

export const VAULT_NAV_EVENT = "vault:navigate";
/** Requests activation of the dynamic Sessions tab before scrolling to it. */
export const VAULT_SCHEDULE_NAV_EVENT = "vault:schedule-navigate";
export const WEBSITE_RECEPTIONIST_OPEN_EVENT = "ic:open-receptionist";

export function findVisibleClassSchedule(): HTMLElement | null {
  if (typeof document === "undefined") return null;
  return (
    Array.from(document.querySelectorAll<HTMLElement>("#class-schedule")).find(
      (element) => element.getClientRects().length > 0,
    ) ?? null
  );
}

// Smooth-scroll the visitor to the class-schedule section (Task 2143) — the
// destination of a booking CTA resolved to the "schedule" kind (see
// resolveBookingCta in shared/footerModel.ts). No-op when the section isn't
// on the page (callers should already have checked scheduleAvailable).
export function scrollToClassSchedule() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(VAULT_SCHEDULE_NAV_EVENT));
  window.requestAnimationFrame(() => {
    findVisibleClassSchedule()?.scrollIntoView({ behavior: "smooth", block: "start" });
  });
}

// Ask the page to navigate to an internal target. Dispatched on window so the
// Services band (which owns tab + carousel state) can handle it wherever the CTA
// lives.
export function requestVaultNavigate(target: VaultNavTarget): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent<VaultNavTarget>(VAULT_NAV_EVENT, { detail: target }));
}

/**
 * Converts an editor CTA destination into a public-site link usable in rich
 * text. These remain same-page URLs rather than persisting CTA-only tokens.
 */
export function websiteCtaDestinationHref(destination: string, publicHomeHref = "/"): string | null {
  const value = destination.trim();
  if (value === WEBSITE_CTA_DESTINATION.HOME) return publicHomeHref;
  if (value === WEBSITE_CTA_DESTINATION.BLOG) return `${publicHomeHref.replace(/\/+$/, "")}/blog`;
  if (value === WEBSITE_CTA_DESTINATION.RECEPTIONIST) return "?digi=general";
  const page = /^page:([a-z0-9]+(?:-[a-z0-9]+)*)$/.exec(value);
  if (page) return `${publicHomeHref.replace(/\/+$/, "")}/${page[1]}`;
  if (value === "receptionist:pricing") return "?digi=pricing";
  if (value === CONSULTATION_SESSION_CTA) return "?session=consultation";
  if (value === "booking") return "#class-schedule";
  const tab = /^tab:(tab-\d+|current-offers|client-feedback|sessions)$/.exec(value);
  if (tab) return `?tab=${toPublicTabKey(tab[1])}`;
  const match = /^(slide|campaign):(\d+)$/.exec(value);
  if (!match) return null;
  return match[1] === "slide" ? `?slide=${match[2]}` : `#go-campaign-${match[2]}`;
}

/** Build a home-page URL for an exact website CTA target, from a legal page. */
export function publicWebsiteTargetHref(
  homeHref: string,
  target: VaultNavTarget | { kind: "schedule" },
): string {
  const base = homeHref.replace(/\/$/, "") || "/";
  if (target.kind === "slide") return `${base}?slide=${encodeURIComponent(target.id)}`;
  if (target.kind === "campaign") return `${base}#go-campaign-${encodeURIComponent(target.id)}`;
  if (target.kind === "tab") return `${base}?tab=${encodeURIComponent(toPublicTabKey(target.key))}`;
  if (target.kind === "session") return `${base}?session=${encodeURIComponent(target.purpose)}`;
  return `${base}?tab=sessions#class-schedule`;
}

function isPublicLegalPath(pathname: string): boolean {
  return (typeof document !== "undefined" && typeof window !== "undefined" &&
    pathname === window.location.pathname && Boolean(document.querySelector('[data-testid="secondary-header"]'))) ||
    pathname === "/terms" || pathname === "/privacy" ||
    /^\/p\/[^/]+\/(?:terms|privacy)\/?$/.test(pathname);
}

/**
 * Identify a link that points at the owner's home site. This lets legal-page
 * CTAs keep owner links in the current tab while true external destinations
 * retain their existing new-tab behavior.
 */
export function isOwnerWebsiteHref(rawHref: string, homeHref?: string): boolean {
  if (typeof window === "undefined" || !homeHref || !isPublicLegalPath(window.location.pathname)) return false;
  try {
    const target = new URL(rawHref, window.location.href);
    const home = new URL(homeHref, window.location.href);
    return target.origin === home.origin && target.pathname.replace(/\/$/, "") === home.pathname.replace(/\/$/, "");
  } catch {
    return false;
  }
}

/** Convert relative in-page targets on a legal document into a home-page URL. */
export function publicWebsiteHrefFromLegalPath(
  rawHref: string,
  homeHref: string,
  currentHref: string,
): string | null {
  let target: URL;
  let current: URL;
  let home: URL;
  try {
    current = new URL(currentHref);
    target = new URL(rawHref, current);
    home = new URL(homeHref, current);
  } catch {
    return null;
  }
  const isLegalPage = isPublicLegalPath(current.pathname);
  const isRelativeToLegalPage = target.pathname === current.pathname;
  const isOwnerHomeLink = target.origin === home.origin && target.pathname === home.pathname;
  if (target.origin !== current.origin || !isLegalPage || (!isRelativeToLegalPage && !isOwnerHomeLink)) {
    return null;
  }
  // Native legal-content anchors should remain native, including when the
  // browser restores them during Back/Forward navigation.
  if (target.hash === `#${LEGAL_CONTENT_ANCHOR}`) return null;
  const hasWebsiteDestination =
    target.searchParams.has("slide") ||
    target.searchParams.has("tab") ||
    target.searchParams.has("session") ||
    target.searchParams.has("digi") ||
    /^#(?:go-(?:slide|campaign|tab)-|class-schedule$|current-specials$|contact$)/i.test(target.hash);
  if (!hasWebsiteDestination) return null;
  target.pathname = home.pathname;
  return `${target.pathname}${target.search}${target.hash}`;
}

/**
 * Turn a link back into this same public website into in-page navigation.
 * Explicit new-window links are filtered by the caller before reaching here.
 * Returns true when the caller should prevent the native click. Legal-page
 * relative targets are routed to the home path with a normal browser navigation.
 */
export function navigateSamePageWebsiteHref(rawHref: string, legalHomeHref?: string): boolean {
  if (typeof window === "undefined") return false;
  if (legalHomeHref) {
    const legalTarget = publicWebsiteHrefFromLegalPath(rawHref, legalHomeHref, window.location.href);
    if (legalTarget) {
      window.location.assign(legalTarget);
      return true;
    }
  }
  let target: URL;
  try {
    target = new URL(rawHref, window.location.href);
  } catch {
    return false;
  }
  if (target.origin !== window.location.origin || target.pathname !== window.location.pathname) {
    return false;
  }
  // Keep legal-reading links native: they create fragment history entries and
  // let the browser restore its own position on Back/Forward.
  if (isPublicLegalPath(target.pathname) && target.hash === `#${LEGAL_CONTENT_ANCHOR}`) {
    return false;
  }

  const slide = target.searchParams.get("slide")?.trim();
  if (slide) {
    requestVaultNavigate({ kind: "slide", id: slide });
    return true;
  }
  if (target.searchParams.get("digi") === "pricing") {
    window.dispatchEvent(
      new CustomEvent(WEBSITE_RECEPTIONIST_OPEN_EVENT, { detail: { flow: "pricing" } }),
    );
    return true;
  }
  if (target.searchParams.get("session") === "consultation") {
    requestVaultNavigate({ kind: "session", purpose: "consultation" });
    return true;
  }
  const campaign = /^#go-campaign-(\d+)$/.exec(target.hash);
  if (campaign) {
    requestVaultNavigate({ kind: "campaign", id: campaign[1] });
    return true;
  }
  const tab = target.searchParams.get("tab")?.trim();
  if (tab) {
    requestVaultNavigate({ kind: "tab", key: tab });
    return true;
  }
  if (target.hash === "#class-schedule") {
    scrollToClassSchedule();
    return true;
  }
  if (target.hash === "#current-specials" || target.hash === "#contact") {
    scrollToServices();
    return true;
  }
  if (target.hash) {
    const destination = document.getElementById(target.hash.slice(1));
    if (!destination) return false;
    window.history.replaceState(window.history.state, "", `${target.pathname}${target.search}${target.hash}`);
    destination.scrollIntoView({ behavior: "smooth", block: "start" });
    return true;
  }
  if (!target.search) {
    window.history.replaceState(window.history.state, "", target.pathname);
    window.scrollTo({ top: 0, behavior: "smooth" });
    return true;
  }
  return false;
}

// Fallback for a CTA with no link set (the no-slug preview and live pages
// before the owner picks a destination): select the Get Started (offers) tab
// and scroll straight to it. Services listens for the #current-specials hash,
// so set the hash without a native jump (replaceState) and announce it, then
// smooth-scroll to the tab anchor — falling back to the section top when the
// page has no offers tab. Shared by the hero CTA and the header CTA.
export function scrollToServices() {
  if (typeof window === "undefined") return;
  window.history.replaceState(null, "", "#current-specials");
  window.dispatchEvent(new HashChangeEvent("hashchange"));
  const el =
    document.getElementById("current-specials") ?? document.getElementById("services");
  el?.scrollIntoView({ behavior: "smooth", block: "start" });
}
